import React, { useRef } from "react";
import { usd, signed, px, tone } from "./floorUtil";

const Spark = ({ pts, up }) => {
  if (pts.length < 2) return null;
  const lo = Math.min(...pts), hi = Math.max(...pts), span = hi - lo || 1;
  const d = pts.map((v, i) => `${(i / (pts.length - 1)) * 100},${28 - ((v - lo) / span) * 26}`).join(" ");
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-7">
      <polyline points={d} fill="none" stroke={up ? "#2CF6B3" : "#FF3D68"} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const Cell = ({ k, v, cls = "text-cyan-50" }) => (<div><p className="fc-label !tracking-[0.15em] !text-[9px]">{k}</p><p className={`fc-num text-[11px] ${cls}`}>{v}</p></div>);

const Card = ({ p, hist, hidden, equity }) => {
  const pct = Number(p.pnl_pct || 0), dollars = Number(p.notional || 0) * pct / 100;
  const short = /short|sell|no/i.test(p.side || "");
  return (
    <div className="fc-panel p-3 space-y-2" data-testid={`floor-position-${p.symbol}`} style={{ borderLeft: `3px solid ${pct >= 0 ? "#2CF6B3" : "#FF3D68"}` }}>
      <div className="flex items-center gap-2">
        <span className="fc-num font-bold text-cyan-50 text-sm">{p.symbol}</span>
        <span className={`fc-num text-[9px] px-1.5 py-0.5 rounded ${short ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"}`}>{(p.side || "").toUpperCase()}</span>
        <span className="fc-num text-[9px] text-cyan-300/50 uppercase truncate">{p.venue}</span>
        <span className={`ml-auto fc-num text-xs font-bold ${tone(pct)}`} data-testid={`floor-position-pnl-${p.symbol}`}>{pct >= 0 ? "+" : ""}{pct.toFixed(2)}%</span>
      </div>
      <div className="grid grid-cols-3 gap-x-2 gap-y-1">
        <Cell k="Size" v={hidden ? (equity ? `${(Number(p.notional || 0) / equity * 100).toFixed(1)}%` : "—") : usd(p.notional, false, 0)} />
        <Cell k="Entry" v={px(p.entry)} />
        <Cell k="Now" v={px(p.current)} cls={tone(pct)} />
        <Cell k="Stop" v={px(p.stop)} cls="fc-loss" />
        <Cell k="Target" v={px(p.target)} cls="fc-gain" />
        <Cell k="P&L" v={hidden ? `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%` : signed(dollars)} cls={tone(dollars)} />
      </div>
      <Spark pts={hist} up={pct >= 0} />
    </div>
  );
};

export const PositionCards = ({ positions, hidden, equity, className = "" }) => {
  const hist = useRef({});
  const open = (positions || []).filter((p) => p.status === "open");
  open.forEach((p) => {
    const h = (hist.current[p.id] = hist.current[p.id] || [Number(p.entry)]);
    if (p.current != null && h.at(-1) !== Number(p.current)) h.push(Number(p.current));
    if (h.length > 40) h.shift();
  });
  return (
    <section className={`flex flex-col min-h-0 ${className}`} data-testid="floor-positions">
      <p className="fc-label mb-2 px-1">Live positions{hidden ? "" : ` · ${open.length}`}</p>
      <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto space-y-2 pr-1">
        {open.length === 0 ? <p className="fc-num text-xs text-cyan-200/40 text-center py-8">Book is flat.</p>
          : open.map((p) => <Card key={p.id} p={p} hist={hist.current[p.id]} hidden={hidden} equity={equity} />)}
      </div>
    </section>
  );
};
